import * as React from 'react';
import { Button } from 'Src/components/Button/Button';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { TwoPanelEditor } from 'Src/components/TwoPanelEditor/TwoPanelEditor';
import { ColumnDefinition, TABLE_SORT_DIRECTION } from 'Src/helpers/table/table';

type Props = {
  isOpen: boolean;
  inviteInvitesOnly: boolean;
  inviteErr: string;
  inviteEligibleLoading: boolean;
  inviteInvitedLoading: boolean;

  onClose: () => void;
  onSave: () => void;

  inviteEligibleItems: any[];
  inviteEligibleSelected: any[];
  setInviteEligibleSelected: React.Dispatch<React.SetStateAction<any[]>>;

  inviteInvitedItems: any[];
  inviteInvitedItemsSorted: any[];
  inviteInvitedSelected: any[];
  setInviteInvitedSelected: React.Dispatch<React.SetStateAction<any[]>>;

  inviteFiltersDraft: any;
  invitePresetFilters: Record<string, string[]>;
  setInviteFiltersDraft: React.Dispatch<React.SetStateAction<any>>;
  inviteItemsFiltersOptions: any;
  inviteShowEligibleFilter: boolean;
  setInviteShowEligibleFilter: React.Dispatch<React.SetStateAction<boolean>>;

  inviteTotal: number;
  invitePage: number;

  loadInvitePage: (page: number, filtersAppliedOverride?: any) => void;
  setInviteFiltersApplied: React.Dispatch<React.SetStateAction<any>>;
  replaceInviteFilters: (filters: any) => void;
  inviteSearch: string;
  setInviteSearch: (value: string) => void;
  setInvitePage: React.Dispatch<React.SetStateAction<number>>;

  INVITE_FILTERS_EMPTY: any;
  INVITE_ITEMS_COLUMNS: ColumnDefinition[];

  onAddClick: () => void;
  onRemoveClick: () => void;

  inviteItemsAddBuffer: any[];
  inviteItemsRemoveBuffer: any[];

  contextTitle?: string;

  onAddSingleItem: (item: any) => void;
  onRemoveSingleItem: (item: any) => void;
  loadAllInviteEligible: () => Promise<any[]>;
  setInviteEligibleLoading: React.Dispatch<React.SetStateAction<boolean>>;
  inviteInitialInvitedIds: Set<string>;
};

export const ClubProgrammeManageClubsInviteModal: React.FC<Props> = ({
  isOpen,
  inviteInvitesOnly,
  inviteErr,
  inviteEligibleLoading,
  inviteInvitedLoading,
  onClose,
  onSave,
  inviteEligibleItems,
  inviteEligibleSelected,
  setInviteEligibleSelected,
  inviteInvitedItems,
  inviteInvitedItemsSorted,
  inviteInvitedSelected,
  setInviteInvitedSelected,
  inviteFiltersDraft,
  invitePresetFilters,
  setInviteFiltersDraft,
  inviteItemsFiltersOptions,
  inviteShowEligibleFilter,
  setInviteShowEligibleFilter,
  inviteTotal,
  invitePage,
  loadInvitePage,
  setInviteFiltersApplied,
  replaceInviteFilters,
  inviteSearch,
  setInviteSearch,
  setInvitePage,
  INVITE_FILTERS_EMPTY,
  INVITE_ITEMS_COLUMNS,
  onAddClick,
  onRemoveClick,
  inviteItemsAddBuffer,
  inviteItemsRemoveBuffer,
  contextTitle,
  onAddSingleItem,
  onRemoveSingleItem,
  loadAllInviteEligible,
  setInviteEligibleLoading,
  inviteInitialInvitedIds
}) => {
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = React.useState(false);

  const hasUnsavedChanges = (inviteItemsAddBuffer?.length || 0) > 0 || (inviteItemsRemoveBuffer?.length || 0) > 0;

  const handleClose = () => {
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }
    setIsCloseConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setIsCloseConfirmOpen(false);
    onClose();
  };

  const handleCancelClose = () => {
    setIsCloseConfirmOpen(false);
  };

  return (
    <>
      <SimpleModal
        isOpen={isOpen}
        customClass="cpInvitePickerModal"
        title={
          <div className="cpInvitePickerTitle">
            <span>Invite list</span>
            {contextTitle ? <span>{contextTitle.replace(/\s*-\s*Invite list\s*$/i, '')}</span> : null}
          </div>
        }
        buttonCancelText="Close"
        onCloseClick={handleClose}
        dialogStyle={{ maxWidth: 1200, width: '95vw' }}
        showFooter={false}
      >
        <div className="cpInvitePicker">
          <div className="cpInvitePickerBody">
            {inviteInvitesOnly ? null : (
              <div className="cpInvitePickerNotice">
                Note: this club is not set to "Invites only". The invite list will be saved, but it won't restrict
                visibility until "Invites only" is enabled.
              </div>
            )}

            {inviteErr ? <div className="cpInvitePickerError">{inviteErr}</div> : null}

            <div className="cpInvitePickerEditor">
              <TwoPanelEditor
                isSourceFirst
                items={inviteEligibleItems}
                itemsTitle="Eligible students"
                itemsColumns={INVITE_ITEMS_COLUMNS}
                itemsSelected={inviteEligibleSelected}
                onItemClick={(index: number) => {
                  const item = inviteEligibleItems[index];
                  if (!item) return;
                  setInviteEligibleSelected(prev => {
                    const exists = prev.some(p => String(p.id) === String(item.id));
                    return exists ? prev.filter(p => String(p.id) !== String(item.id)) : [...prev, item];
                  });
                }}
                itemsFilters={inviteFiltersDraft}
                itemsPresetFilters={invitePresetFilters}
                itemsPresetSourceLabel="club"
                itemsFiltersOptions={inviteItemsFiltersOptions}
                onReplaceItemsFilters={replaceInviteFilters}
                onItemsSearchChange={setInviteSearch}
                itemsSearchValue={inviteSearch}
                isShowItemsFilter={inviteShowEligibleFilter}
                isItemsFiltered={
                  !!(
                    String(inviteFiltersDraft?.firstName || '').trim() ||
                    String(inviteFiltersDraft?.lastName || '').trim() ||
                    (inviteFiltersDraft?.forms || []).length ||
                    (inviteFiltersDraft?.ages || []).length ||
                    (inviteFiltersDraft?.gender || []).length
                  )
                }
                onItemsFilterClick={(e: any) => {
                  e.preventDefault();
                  setInviteShowEligibleFilter(v => !v);
                }}
                onItemsFilterChange={(event: any, filterField: string) => {
                  const filterType = (INVITE_ITEMS_COLUMNS.find(c => c.field === filterField) as any)?.type;
                  if (filterType === 'MULTISELECT') {
                    const opts = event?.target?.options || [];
                    const value: string[] = [];
                    for (let i = 0; i < opts.length; i++) {
                      if (opts[i].selected) value.push(String(opts[i].value));
                    }
                    setInviteFiltersDraft((prev: any) => ({ ...prev, [filterField]: value }));
                  } else {
                    const v = String(event?.target?.value ?? '');
                    setInviteFiltersDraft((prev: any) => ({ ...prev, [filterField]: v }));
                  }
                }}
                itemsOnApplyFilterClick={() => {
                  const next = { ...inviteFiltersDraft };
                  setInviteFiltersApplied(next);
                  setInvitePage(1);
                  loadInvitePage(1, next);
                }}
                itemsOnClearFilterClick={() => {
                  const next = { ...INVITE_FILTERS_EMPTY, ...invitePresetFilters };
                  setInviteFiltersDraft(next);
                  setInviteFiltersApplied(next);
                  setInvitePage(1);
                  loadInvitePage(1, next);
                }}
                itemsCount={(inviteTotal || 0) + (inviteItemsRemoveBuffer || []).length}
                itemCurrentPage={invitePage || 1}
                itemsModernPagination
                itemsSetCurrentPageParams={(p: number) => loadInvitePage(p)}
                onItemAccumulatorClick={(index: number) => {
                  const item = inviteInvitedItemsSorted[index];
                  if (!item) return;
                  setInviteInvitedSelected(prev => {
                    const exists = prev.some(p => String(p.id) === String(item.id));
                    return exists ? prev.filter(p => String(p.id) !== String(item.id)) : [...prev, item];
                  });
                }}
                onSelectAllItemsOnPageClick={() => {
                  setInviteEligibleSelected(prev => {
                    const newItems = inviteEligibleItems.filter(
                      item => !prev.some(p => String(p.id) === String(item.id))
                    );
                    return [...prev, ...newItems];
                  });
                }}
                onSelectAllItemsClick={() => {
                  setInviteEligibleLoading(true);
                  loadAllInviteEligible()
                    .then((all: any[]) => {
                      const normalized = all
                        .map((u: any) => ({
                          ...u,
                          id: String(u?.id || u?._id || ''),
                          permissionId: String(u?.permissionId || '')
                        }))
                        .filter((x: any) => !!x.id && !!x.permissionId);
                      setInviteEligibleSelected(current => {
                        const selectedIds = new Set(current.map(item => `${item.id}_${item.permissionId}`));
                        return [
                          ...current,
                          ...normalized.filter(item => !selectedIds.has(`${item.id}_${item.permissionId}`))
                        ];
                      });
                      setInviteEligibleLoading(false);
                    })
                    .catch(() => setInviteEligibleLoading(false));
                }}
                onUnselectAllItemsClick={() => setInviteEligibleSelected([])}
                isSelectAllItemsChecked={
                  inviteEligibleItems.length > 0 &&
                  inviteEligibleItems.every(item => inviteEligibleSelected.some(p => String(p.id) === String(item.id)))
                }
                sortItemsDirection={'' as TABLE_SORT_DIRECTION}
                sortItemsColumnsName={''}
                onSortItemsClick={() => {}}
                itemsAccumulator={inviteInvitedItemsSorted || []}
                itemsAccumulatorTitle="Invited"
                itemsAccumulatorColumns={INVITE_ITEMS_COLUMNS}
                itemsAccumulatorSelected={inviteInvitedSelected}
                itemsAccumulatorFilters={{}}
                onItemsAccumulatorFilterChange={() => {}}
                isShowItemsAccumulatorFilter={false}
                isItemsAccumulatorFiltered={false}
                onItemsAccumulatorFilterClick={() => {}}
                itemsAccumulatorFiltersOptions={{} as any}
                itemsAccumulatorCount={(inviteInvitedItemsSorted || []).length}
                itemAccumulatorCurrentPage={1}
                itemsAccumulatorOnApplyFilterClick={() => {}}
                itemsAccumulatorOnClearFilterClick={() => {}}
                onSelectAllItemsAccumulatorOnPageClick={() => {
                  setInviteInvitedSelected(prev => {
                    const newItems = (inviteInvitedItems || []).filter(
                      item => !prev.some(p => String(p.id) === String(item.id))
                    );
                    return [...prev, ...newItems];
                  });
                }}
                onSelectAllItemsAccumulatorClick={() => {
                  setInviteInvitedSelected(inviteInvitedItems || []);
                }}
                isSelectAllItemsAccumulatorChecked={
                  (inviteInvitedItems || []).length > 0 &&
                  (inviteInvitedItems || []).every(item =>
                    inviteInvitedSelected.some(p => String(p.id) === String(item.id))
                  )
                }
                onUnselectAllItemsAccumulatorClick={() => setInviteInvitedSelected([])}
                sortItemsAccumulatorDirection={'' as TABLE_SORT_DIRECTION}
                sortItemsAccumulatorColumnsName={''}
                onSortItemsAccumulatorClick={() => {}}
                onAddClick={() => onAddClick()}
                onRemoveClick={onRemoveClick}
                itemsAddBuffer={inviteItemsAddBuffer}
                itemsRemoveBuffer={[]}
                onAddSingleItem={onAddSingleItem}
                onRemoveSingleItem={onRemoveSingleItem}
                isItemsLoading={inviteEligibleLoading}
                isItemsAccumulatorLoading={inviteInvitedLoading}
              />
            </div>
          </div>

          <div className="cpInvitePickerFooter">
            <Button
              text="Cancel"
              onClick={handleClose}
              customClass="sit-btn sit-btn--secondary"
              disabled={inviteEligibleLoading}
            />
            <Button
              text="Save invite list"
              onClick={onSave}
              customClass="sit-btn sit-btn--primary"
              disabled={inviteEligibleLoading}
            />
          </div>
        </div>
      </SimpleModal>

      <SimpleModal
        isOpen={isCloseConfirmOpen}
        title="Discard changes?"
        body="You have unsaved invite list changes. If you close now, those changes will be lost."
        buttonText="Discard changes"
        buttonCancelText="Keep editing"
        onButtonClick={handleConfirmClose}
        onCloseClick={handleCancelClose}
        customClass="modal-md cpInvitePickerDiscardModal"
      />
    </>
  );
};
